import React, { useEffect, useState, useRef } from 'react';
import { useInView, animate } from 'framer-motion';
import BgImage from '../../assets/why_trrip_bg_1769531997717.png';
import landingData from '../landingData.json';

const CountUpAnimation = ({ target, suffix = '+', shouldStart }) => {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (shouldStart) {
      const controls = animate(0, target, {
        duration: 2.2,
        ease: [0.16, 1, 0.3, 1],
        onUpdate: (value) => {
          setDisplay(Math.floor(value));
        }
      });
      return () => controls.stop();
    }
  }, [shouldStart, target]);

  return (
    <span>
      {display.toLocaleString()}<em className="not-italic text-[#E0F2F1]">{suffix}</em>
    </span>
  );
};

const NetworkSection = ({ isDesktop = true }) => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { amount: 0.2, once: true });
  const targetNumbers = [100, 1200, 6500];

  return (
    <section 
      ref={sectionRef}
      className="py-16 sm:py-24 text-white relative overflow-hidden bg-gradient-to-br from-[#044E4D] via-[#067978] to-[#03372C]"
      id="network"
    >
      {/* Global Network Map Background Image */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <img 
          src={BgImage} 
          alt="Global Network" 
          className="w-full h-full object-cover opacity-20 mix-blend-overlay"
        />
      </div>

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 md:px-8 relative z-10">
        {/* Header */}
        <div className="text-center max-w-[760px] mx-auto mb-10 sm:mb-14">
          <div className="flex justify-center mb-4">
            <div 
              className="inline-flex items-center gap-2 text-[10px] sm:text-[11.5px] font-bold tracking-wider uppercase px-3 sm:px-3.5 py-1.5 rounded-full text-white backdrop-blur-md"
              style={{ background: 'rgba(255, 255, 255, 0.15)', border: '1px solid rgba(255, 255, 255, 0.3)' }}
            >
              <i className="ti ti-world text-xs sm:text-sm"></i> Network at a glance
            </div>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-[44px] font-extrabold text-white tracking-tight mb-3 sm:mb-4" style={{ lineHeight: '1.45' }}>
            Built where travel<br />
            <span className="text-[#E0F2F1]">actually happens.</span>
          </h2>
          <p className="text-white/85 font-normal text-xs sm:text-base md:text-[16.5px] leading-[1.6] max-w-[560px] mx-auto">
            An on-trip layer connecting agents, travellers, and local businesses across the destinations that matter most.
          </p>
        </div>

        {/* Original Glassmorphic Container Stage */}
        <div 
          className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 max-w-[1000px] mx-auto p-6 sm:p-8 md:p-10 backdrop-blur-md"
          style={{
            background: 'rgba(255, 255, 255, 0.08)',
            border: '1px solid rgba(255, 255, 255, 0.18)',
            borderRadius: isDesktop ? '24px' : '18px'
          }}
        >
          {landingData.networkStats.map((stat, i) => (
            <div key={i} className="flex flex-col items-center sm:items-start text-center sm:text-left">
              <div 
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center text-white text-xl sm:text-2xl mb-3 sm:mb-4"
                style={{ background: 'rgba(255, 255, 255, 0.15)' }}
              >
                <i className={stat.iconClass}></i>
              </div>
              
              <div className="text-3xl sm:text-4xl md:text-[48px] font-extrabold text-white leading-none tracking-tight mb-2">
                <CountUpAnimation target={targetNumbers[i]} suffix="+" shouldStart={isInView} />
              </div>

              <div className="text-[10px] sm:text-xs font-bold tracking-widest uppercase text-white/90 pb-2 sm:pb-3 border-b border-white/20 w-full mb-2 sm:mb-3">
                {stat.label}
              </div>

              <p className="text-xs sm:text-[13.5px] text-white/75 leading-[1.55]">{stat.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default NetworkSection;
